import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import TopNav from "./components/TopNav.jsx";
import BottomNav from "./components/BottomNav.jsx";
import { FleetProvider } from "./contexts/FleetContext.jsx";
import {
  useFleetCommands,
  useFleetDevices,
  useFleetStatus,
} from "./hooks/useFleet.js";
import { useSelectedVehicle } from "./hooks/useSelectedVehicle.js";
import { useGeofences } from "./hooks/useGeofences.js";
import { useGroups } from "./hooks/useGroups.js";
import { useSettings } from "./hooks/useSettings.js";
import { useAlerts } from "./hooks/useAlerts.js";
import { useServiceWorkerMessages } from "./hooks/useServiceWorkerMessages.js";
import { useVoiceAnnouncements } from "./hooks/useVoiceAnnouncements.js";
import { AuthProvider } from "./context/AuthContext.jsx";
import { ThemeProvider } from "./hooks/useTheme.jsx";
import { NavBadgeCountsProvider } from "./context/NavBadgeCountsContext.jsx";
import { ToastProvider } from "./hooks/useToast.jsx";
import { FinanzasTenantProvider } from "./context/FinanzasTenantContext.jsx";
import BillingSuspendedOverlay from "./components/BillingSuspendedOverlay.jsx";
import { ProtectedRoute } from "./components/ProtectedRoute.jsx";
import RoleRoute from "./components/RoleRoute.jsx";
import { ROLES } from "./lib/roles.js";
import {
  WebSocketProvider,
  useOptionalWebSocketContext,
} from "./contexts/WebSocketContext.jsx";
import useSmartLoading from "./hooks/useSmartLoading.js";

// Gracia antes de mostrar el banner "sin conexión": una reconexión normal
// tarda menos de 2 s (y en la app nativa ocurre en CADA vuelta del background
// cuando el OS mató el socket). Mostrarlo en el instante en que cae `connected`
// convertía cada regreso en un flash rojo por un evento interno y esperado; si
// a los 4 s sigue caído, ahí sí es una desconexión que el usuario debe ver.
const OFFLINE_BANNER_GRACE_MS = 4_000;
import { RelativeTimeTickProvider } from "./contexts/RelativeTimeTickContext.jsx";
import LoadingOverlay from "./components/LoadingOverlay.jsx";
import RequirePermission from "./components/RequirePermission.jsx";
import InstallPrompt from "./components/pwa/InstallPrompt.jsx";
import UpdateBanner from "./components/pwa/UpdateBanner.jsx";
import { usePushDeepLink } from "./hooks/usePushDeepLink.js";
import { registerDeviceToken } from "./lib/pushMobile.js";

// ── Lazy-loaded routes — each becomes its own chunk on the network ──
const MonitorPage = lazy(() => import("./pages/MonitorPage.jsx"));
const VehiculosPage = lazy(() => import("./pages/admin/sections/Flota/VehiculosPage.jsx"));
const ConductoresPage = lazy(() => import("./pages/admin/sections/Flota/ConductoresPage.jsx"));
const ConductorPerfilPage = lazy(() => import("./pages/admin/sections/Flota/ConductorPerfilPage.jsx"));
const DispositivosPage = lazy(() => import("./pages/admin/sections/Flota/DispositivosPage.jsx"));
const SimsPage = lazy(() => import("./pages/admin/sections/Flota/SimsPage.jsx"));
const GruposPanel = lazy(() => import("./pages/admin/sections/Flota/GruposPanel.jsx"));
const CommandsPage = lazy(() => import("./pages/CommandsPage.jsx"));
const AlertsPage = lazy(() => import("./pages/AlertsPage.jsx"));
const DashboardPage = lazy(() => import("./pages/DashboardPage.jsx"));
const AlertasPage = lazy(() => import("./pages/admin/sections/Control/AlertasPage.jsx"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage.jsx"));
const ReportesPage = lazy(() => import("./pages/ReportesPage.jsx"));
const RecorridosPage = lazy(() => import("./pages/reports/RecorridosPage.jsx"));
const VelocidadPage = lazy(() => import("./pages/reports/VelocidadPage.jsx"));
const GeocercasPage = lazy(() => import("./pages/GeocercasPage.jsx"));
const GeofenceWizardPage = lazy(() => import("./pages/geofence-wizard/GeofenceWizardPage.jsx"));
const LoginPage = lazy(() => import("./pages/LoginPage.jsx"));
const OnboardingPage = lazy(() => import("./pages/onboarding/OnboardingPage.jsx"));
const KycDashboardPage = lazy(() => import("./pages/admin/sections/Kyc/KycDashboardPage.jsx"));
const KycModulePage = lazy(() => import("./pages/admin/sections/Modulos/KycModulePage.jsx"));
const GpsModulePage = lazy(() => import("./pages/admin/sections/Modulos/GpsModulePage.jsx"));
const RutasPage = lazy(() => import("./pages/admin/sections/Modulos/RutasPage.jsx"));
const ContabilidadPage = lazy(() => import("./pages/admin/sections/Modulos/ContabilidadPage.jsx"));
const ClientesPage = lazy(() => import("./pages/admin/sections/Cobros/ClientesPage.jsx"));
const SuscripcionesPage = lazy(() => import("./pages/admin/sections/Cobros/SuscripcionesPage.jsx"));
const AjustesPage = lazy(() => import("./pages/admin/sections/Cobros/AjustesPage.jsx"));
const FacturasPage = lazy(() => import("./pages/admin/sections/Cobros/FacturasPage.jsx"));
const PagosPage = lazy(() => import("./pages/admin/sections/Cobros/PagosPage.jsx"));
const MoraPage = lazy(() => import("./pages/admin/sections/Cobros/MoraPage.jsx"));
const PlanillaPage = lazy(() => import("./pages/admin/sections/Finanzas/PlanillaPage.jsx"));
const UnidadesPage = lazy(() => import("./pages/admin/sections/Finanzas/UnidadesPage.jsx"));
const MovimientosPage = lazy(() => import("./pages/admin/sections/Finanzas/MovimientosPage.jsx"));
const MantenimientosPage = lazy(
  () => import("./pages/admin/sections/Finanzas/MantenimientosPage.jsx"),
);
const ObligacionesPage = lazy(
  () => import("./pages/admin/sections/Finanzas/ObligacionesPage.jsx"),
);
const ArqueoPage = lazy(() => import("./pages/admin/sections/Finanzas/ArqueoPage.jsx"));
const CierrePage = lazy(() => import("./pages/admin/sections/Finanzas/CierrePage.jsx"));
const FinanzasDashboardPage = lazy(
  () => import("./pages/admin/sections/Finanzas/DashboardPage.jsx"),
);
const ColaVisitasView = lazy(() => import("./pages/admin/sections/Revisiones/ColaVisitasView.jsx"));
const InventarioSaludView = lazy(() => import("./pages/admin/sections/Revisiones/InventarioSaludView.jsx"));
const HistorialSesionesView = lazy(() => import("./pages/admin/sections/Revisiones/HistorialSesionesView.jsx"));
const ProfileLayout = lazy(() => import("./pages/profile/ProfileLayout.jsx"));
const AlertsConfigPage = lazy(() => import("./pages/profile/AlertsConfigPage.jsx"));
const DeviceOnboardingWizardPage = lazy(() =>
  import("./pages/onboarding-wizard/DeviceOnboardingWizardPage.jsx"),
);
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout.jsx"));
const InicioPage = lazy(() => import("./pages/admin/InicioPage.jsx"));
const UsersPage = lazy(() => import("./pages/admin/sections/Usuarios/UsersPage.jsx"));
const SubusersPage = lazy(() => import("./pages/admin/sections/Usuarios/SubusersPage.jsx"));
const AccesosPage = lazy(() => import("./pages/admin/sections/Usuarios/AccesosPage.jsx"));
const PlanesPage = lazy(() => import("./pages/admin/sections/Plataforma/PlanesPage.jsx"));
const FeaturesPage = lazy(() => import("./pages/admin/sections/Plataforma/FeaturesPage.jsx"));

// Lightweight fallback during chunk fetch — matches LoadingOverlay style.
function RouteFallback() {
  return <LoadingOverlay show message="Cargando" />;
}

/**
 * ROUTE_GUARDS — declarative role-guard map for the three protected routes.
 *
 * Exported so routing.test.jsx can import the real config instead of
 * duplicating it. If a guard changes here the test will catch the regression.
 *
 * Shape: { path: string, allow: string[], fallback: string }[]
 */
export const ROUTE_GUARDS = [
  {
    path: "/dashboard",
    allow: [ROLES.MASTER, ROLES.OWNER],
    fallback: "/",
  },
];

/**
 * Redirect que conserva la query string. `<Navigate to="/x"/>` la tira, y
 * `/control/comandos?imei=…` es el enlace con el que la ficha de un vehículo
 * salta a sus comandos: sin la query se llega a la lista sin nada elegido.
 */
function LegacyRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/**
 * La app nativa llega a TODA la aplicación. Ya no queda guard por dispositivo
 * —ni acá ni en ningún otro archivo—: las rutas de abajo son las mismas en el
 * teléfono y en el navegador.
 *
 * Hubo una lista blanca (Monitor + Perfil) bajo la premisa de que el móvil era
 * "de vigilancia, no de administración". Se borró entera —constante y guard—
 * porque el usuario del móvil resultó ser el mismo que administra la flota:
 * pedía tocar umbrales, ver reportes y revisar el dashboard desde el teléfono,
 * y lo único que hacía el guard era rebotarlo al mapa sin explicar por qué.
 *
 * Lo único que filtra son los PERMISOS: `RequirePermission` y `RoleRoute`
 * valen igual en móvil que en escritorio. La restricción por dispositivo era
 * una decisión de producto, no de seguridad.
 *
 * Si alguna pantalla no da en pantalla chica, el arreglo es esa pantalla — no
 * volver a esconder media app.
 */

/**
 * AppShell — Only rendered when authenticated.
 *
 * Su único trabajo es montar el proveedor de la flota. La flota YA NO cuelga
 * de acá como estado: si lo hiciera, cada mensaje WS re-renderizaría este nodo
 * y con él la página entera, que es el cuello de botella que la fase 3
 * elimina. Ver `contexts/FleetContext.jsx`.
 *
 * Este componente solo se re-renderiza cuando cambian los ajustes.
 */
function AppShell() {
  const { settings, updateSettings } = useSettings();
  return (
    <FleetProvider tracePositions={settings.tracePositions}>
      <AppChrome settings={settings} updateSettings={updateSettings} />
    </FleetProvider>
  );
}

/**
 * AppChrome — navegación, overlay de carga y tabla de rutas.
 *
 * Se suscribe SOLO a estado agregado que cambia unas pocas veces por sesión:
 * `loading` del store de flota (carga inicial) y `connected` del contexto WS
 * (caídas/reconexiones del socket). Los datos por vehículo los pide cada
 * pantalla por su cuenta.
 */
function AppChrome({ settings, updateSettings }) {
  const { loading: devicesLoading } = useFleetStatus();
  // Estado del socket directo del contexto — fuente única acá, sin pasar por
  // el espejo del fleetStore (ese espejo queda para consumidores que solo ven
  // el store — hoy, la vista legacy de tests). Optional: un contexto ausente
  // no debe tumbar el shell entero.
  const wsCtx = useOptionalWebSocketContext();
  const wsConnected = wsCtx?.connected ?? false;
  const isSocketOpen = wsCtx?.isSocketOpen;
  // Gracia de 4 s (useSmartLoading: delay antes de mostrar, cancelado si
  // reconecta a tiempo; minDuration 0 = al reconectar se oculta en el acto):
  // una reconexión normal —que en la app nativa pasa en CADA vuelta del
  // background— termina antes y el banner ni aparece.
  const showOfflineBanner = useSmartLoading(!wsConnected, {
    delay: OFFLINE_BANNER_GRACE_MS,
    minDuration: 0,
  });
  const { selectedImei, selectDevice, listOpen, openList, closeList } = useSelectedVehicle();
  const geofencesState = useGeofences();
  const groupsState = useGroups();
  const { unreadCount, clearUnread } = useAlerts();

  // Listen for push notification clicks from the Service Worker.
  // Hook auto-plays CRITICAL alert sound (autoplay allowed — user tap origin).
  useServiceWorkerMessages(() => {});

  // Locución por voz de las alertas que llegan por WebSocket. Va en AppShell y
  // no en una página concreta para que hable en cualquier pantalla de la app —
  // el operador puede estar en Reportes cuando entra un SOS. No hace nada
  // mientras el usuario no lo active en /perfil/preferencias.
  useVoiceAnnouncements();

  // Push nativo: al tocar la notificación abre el vehículo, y al arrancar
  // registra el token de este equipo. Los dos son no-op en web (isNative false).
  usePushDeepLink();
  useEffect(() => {
    registerDeviceToken();
  }, []);

  // Overlay bloqueante SOLO en la carga inicial (devicesLoading): en un F5
  // frío no hay nada que mostrar y toda página gana con la señal "arrancando".
  // Los resyncs de reconexión ya NO bloquean: la app sigue mostrando el último
  // estado conocido y el catch-up lo comunica la propia animación de sync del
  // marcador (que el overlay antiguo tapaba con blur). En la app nativa el
  // socket muere en CADA vuelta del background, así que aquel overlay
  // convertía cada regreso en una pantalla "Reconectando al servidor".
  // Labels are rendered by <RadarLoaderLarge/> which already animates its
  // own ellipsis dots — DO NOT append "…" here or we'd get double dots.
  const showOverlay = devicesLoading;

  return (
    // Las alertas sin leer llegan por contexto y no por prop: TRES pantallas
    // dibujan ese globo desde puntos distintos del árbol, y un cableado perdido
    // en un refactor de rutas dejaba el suyo en cero sin que nada se quejara.
    // Ver context/NavBadgeCountsContext.jsx.
    <NavBadgeCountsProvider alerts={unreadCount}>
    <div className="flex flex-col h-screen bg-background-dark canvas-wash font-display">
      <LoadingOverlay show={showOverlay} message="Cargando datos" />

      {/* WS offline banner. Doble filtro para no gritar de más:
          - gracia de 4 s (useSmartLoading): las reconexiones normales
            terminan antes y el banner ni aparece;
          - doble check contra el readyState real del socket: si `connected`
            quedó stale tras una race pero el socket está OPEN, no mentir.
          El contenedor NO captura input (pointer-events-none): la franja se
          superpone al tope del TopNav y sus taps deben pasar de largo. Solo el
          botón compacto de reintento es interactivo — el escape hatch de la
          app nativa, donde recargar la página no existe. */}
      {showOfflineBanner && !isSocketOpen?.() && (
        <div className="absolute top-[env(safe-area-inset-top)] left-0 right-0 z-[1000] bg-danger text-white text-center text-sm py-1.5 font-semibold pointer-events-none">
          Sin conexión en tiempo real —{" "}
          <button
            type="button"
            onClick={() => wsCtx?.forceReconnect?.()}
            className="pointer-events-auto underline underline-offset-2 cursor-pointer"
          >
            reintentar ahora
          </button>
        </div>
      )}

      <TopNav wsConnected={wsConnected} />

      {/* PWA: ambos se auto-ocultan si no aplican (ya instalada / sin update). */}
      <UpdateBanner />
      <InstallPrompt />

      {/* pb: alto de la BottomNav + safe area del home indicator (PWA instalada). */}
      <main className="flex-1 overflow-hidden relative pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* ── Core screens ── */}
            <Route
              path="/"
              element={
                <MonitorPage
                  selectedImei={selectedImei}
                  selectDevice={selectDevice}
                  listOpen={listOpen}
                  openList={openList}
                  closeList={closeList}
                  geofencesState={geofencesState}
                  trailColor={settings.trailColor}
                  clusterEnabled={settings.clusterEnabled}
                  clusterPreset={settings.clusterPreset}
                  groups={groupsState.groups}
                />
              }
            />

            <Route
              path="/dashboard"
              element={
                <RoleRoute
                  allow={[ROLES.MASTER, ROLES.OWNER]}
                  fallback="/"
                >
                  <FleetListRoute
                    render={(devices) => (
                      <DashboardPage
                        devices={devices}
                        wsConnected={wsConnected}
                        groups={groupsState.groups}
                      />
                    )}
                  />
                </RoleRoute>
              }
            />

            {/* ── Control legacy → /admin/control/* ──
                Comandos, geocercas y la configuración de alertas se mudaron al
                rail de /admin. La pantalla-índice /control (la rejilla de
                tarjetas de móvil) desapareció con ellas: el menú del shell ya
                lista los tres destinos. */}
            <Route
              path="/control"
              element={<Navigate to="/admin/control/comandos" replace />}
            />
            <Route
              path="/control/geocercas"
              element={<LegacyRedirect to="/admin/control/geocercas" />}
            />
            <Route
              path="/control/geocercas/nueva"
              element={<LegacyRedirect to="/admin/control/geocercas/nueva" />}
            />
            <Route
              path="/control/comandos"
              element={<LegacyRedirect to="/admin/control/comandos" />}
            />

            <Route
              path="/reportes"
              element={<ReportesPage />}
            />
            <Route path="/reportes/recorridos" element={<RecorridosPage />} />
            <Route path="/reportes/velocidad" element={<VelocidadPage />} />

            <Route
              path="/analisis"
              element={
                <AlertsPage
                  clearUnread={clearUnread}
                  groups={groupsState.groups}
                />
              }
            />

            {/* ── Gestión legacy → /admin redirects (1-month transition) ── */}
            <Route path="/gestion" element={<Navigate to="/admin" replace />} />
            <Route
              path="/gestion/tenants"
              element={<Navigate to="/admin/usuarios/usuarios" replace />}
            />
            <Route
              path="/gestion/asignaciones"
              element={
                <Navigate to="/admin/usuarios/accesos-subusuarios" replace />
              }
            />
            <Route
              path="/gestion/usuarios"
              element={<Navigate to="/admin/usuarios/subusuarios" replace />}
            />
            <Route
              path="/gestion/dispositivos"
              element={<Navigate to="/admin/flota/dispositivos" replace />}
            />
            <Route
              path="/gestion/dispositivos/wizard"
              element={<Navigate to="/admin/flota/dispositivos/wizard" replace />}
            />
            <Route
              path="/gestion/conductores"
              element={<Navigate to="/admin/flota/conductores" replace />}
            />
            <Route
              path="/gestion/vehiculos"
              element={<Navigate to="/admin/flota/vehiculos" replace />}
            />
            <Route
              path="/gestion/grupos"
              element={<Navigate to="/admin/flota/grupos" replace />}
            />
            <Route
              path="/gestion/kyc"
              element={<Navigate to="/admin/kyc" replace />}
            />

            {/* ── Perfil (PR-B.5) ── */}
            {/*
              Single-page inline scroll: all five sections (Cuenta, Seguridad,
              Preferencias, Plan, Facturación) render in one scrollable container.
              The sidebar acts as anchor-scroll navigation, not route navigation.
              ProfileLayout imports sub-pages directly and passes props.
            */}
            <Route
              path="/perfil"
              element={
                <ProfileLayout
                  settings={settings}
                  updateSettings={updateSettings}
                />
              }
            />
            {/*
              Todo lo que se configura por tipo de alerta —canales, urgencia,
              vehículos, umbral, locución— vive junto en /admin/control/alertas,
              y ahí va a parar quien tenga `manage_alerts`: esta ruta lo
              redirige. Desde el teléfono se alcanza igual que desde escritorio
              — la lista blanca de rutas nativas ya no existe.

              Esta segunda entrada nació cuando /admin sí quedaba fuera del
              móvil, y se queda porque el permiso NO puede ser la puerta de
              todo: por dónde te llega una alerta y si se locuta es preferencia
              de tu cuenta. `AlertsConfigPage` evalúa el permiso por dentro y le
              sirve a los demás roles la versión reducida, en vez de rebotarlos.
            */}
            <Route path="/perfil/alertas" element={<AlertsConfigPage />} />

            {/* ── Backwards-compat redirects ── */}
            <Route
              path="/commands"
              element={<LegacyRedirect to="/admin/control/comandos" />}
            />
            <Route path="/alerts" element={<Navigate to="/analisis" replace />} />
            {/*
              PR-B.5: legacy /configuracion + /settings now point at the
              new preferences sub-route directly (no double-hop, per PR-B
              HIGH-1 lesson). 1-month transition window matches PR-B.
            */}
            <Route
              path="/configuracion"
              element={<Navigate to="/perfil" replace />}
            />
            <Route path="/settings" element={<Navigate to="/perfil" replace />} />

            {/* ── Admin (PR-B) ── */}
            {/*
              New /admin shell. Children are mounted progressively across PR-B
              commits 2-6; commit 7 adds /gestion/* redirects. RoleRoute keeps
              viewers out of the entire surface (only master+operator allowed).
              Each child route is additionally gated by <RequirePermission/>.
            */}
            <Route
              path="/admin"
              element={
                <RoleRoute
                  allow={[ROLES.MASTER, ROLES.OWNER]}
                  fallback="/"
                >
                  <AdminLayout />
                </RoleRoute>
              }
            >
              {/* La home del panel. Sin permiso propio: cualquiera que llegue
                  a /admin puede verla, y cada bloque de adentro se esconde
                  solo cuando el rol o el plan no dan. */}
              <Route index element={<InicioPage />} />
              <Route
                path="usuarios/usuarios"
                element={
                  <RequirePermission action="manage_operators">
                    <UsersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="usuarios/accesos"
                element={
                  <RequirePermission action="manage_operators">
                    <AccesosPage
                      roleFilter="owner"
                      title="Accesos de usuarios"
                      subtitle="Qué dispositivos ve cada usuario."
                      emptyHint="No hay dispositivos disponibles para asignar a este usuario."
                    />
                  </RequirePermission>
                }
              />
              <Route
                path="usuarios/subusuarios"
                element={
                  <RequirePermission action="view_viewers">
                    <SubusersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="usuarios/accesos-subusuarios"
                element={
                  <RequirePermission action="view_viewers">
                    <AccesosPage
                      roleFilter="member"
                      title="Accesos de subusuarios"
                      subtitle="Qué dispositivos ve cada subusuario."
                    />
                  </RequirePermission>
                }
              />
              {/* TODO: remove redirects after 2026-08-23 (90 days) */}
              <Route path="usuarios/operators" element={<Navigate to="/admin/usuarios/usuarios" replace />} />
              <Route path="usuarios/viewers" element={<Navigate to="/admin/usuarios/subusuarios" replace />} />
              <Route
                path="flota/dispositivos"
                element={
                  <RequirePermission action="view_devices">
                    <DispositivosPage />
                  </RequirePermission>
                }
              />
              <Route
                path="flota/pendientes"
                element={
                  <RequirePermission action="view_devices">
                    <DispositivosPage />
                  </RequirePermission>
                }
              />
              <Route
                path="flota/dispositivos/wizard"
                element={
                  <RequirePermission action="manage_devices">
                    <DeviceOnboardingWizardPage />
                  </RequirePermission>
                }
              />
              <Route
                path="flota/sims"
                element={
                  <RequirePermission action="manage_devices">
                    <SimsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="flota/conductores"
                element={
                  <RequirePermission action="manage_drivers">
                    <ConductoresPage />
                  </RequirePermission>
                }
              />
              {/* El perfil cuelga de la tabla: findSectionIdForPath matchea por
                  límite de segmento, así que /conductores/:id deja el rail
                  marcado en Flota › Conductores. */}
              <Route
                path="flota/conductores/:driverId"
                element={
                  <RequirePermission action="manage_drivers">
                    <ConductorPerfilPage />
                  </RequirePermission>
                }
              />
              <Route
                path="flota/vehiculos"
                element={
                  <RequirePermission action="manage_vehicles">
                    <VehiculosPage />
                  </RequirePermission>
                }
              />
              <Route
                path="flota/grupos"
                element={
                  <RequirePermission action="manage_vehicles">
                    <GruposPanel />
                  </RequirePermission>
                }
              />
              {/* ── Control ──
                  Las tres pantallas del viejo menú "Control" del TopNav. Son
                  pantallas de configuración, pero se apoyan en los mismos datos
                  que el mapa, así que toman la flota por <FleetListRoute/> — el
                  envoltorio que la suscribe a `useFleetDevices()`. NO se pasa
                  desde <AppShell/>: la fase 4 sacó la lista del shell para que
                  un cambio de un dispositivo no re-renderice la app entera. */}
              <Route
                path="control/comandos"
                element={
                  <RequirePermission action="send_commands">
                    <CommandsRoute groups={groupsState.groups} />
                  </RequirePermission>
                }
              />
              <Route
                path="control/geocercas"
                element={
                  <RequirePermission action="manage_geofences">
                    <FleetListRoute
                      render={(devices) => (
                        <GeocercasPage
                          geofencesState={geofencesState}
                          devices={devices}
                          groups={groupsState.groups}
                        />
                      )}
                    />
                  </RequirePermission>
                }
              />
              <Route
                path="control/geocercas/nueva"
                element={
                  <RequirePermission action="manage_geofences">
                    <FleetListRoute
                      render={(devices) => (
                        <GeofenceWizardPage
                          geofencesState={geofencesState}
                          devices={devices}
                          groups={groupsState.groups}
                        />
                      )}
                    />
                  </RequirePermission>
                }
              />
              <Route
                path="control/alertas"
                element={
                  <RequirePermission action="manage_alerts">
                    <FleetListRoute
                      render={(devices) => (
                        <AlertasPage
                          devices={devices}
                          groups={groupsState.groups}
                        />
                      )}
                    />
                  </RequirePermission>
                }
              />
              <Route
                path="kyc"
                element={
                  <RequirePermission action="view_kyc">
                    <KycDashboardPage />
                  </RequirePermission>
                }
              />
              <Route
                path="revisiones/cola"
                element={
                  <RequirePermission action="view_inspections">
                    <ColaVisitasView />
                  </RequirePermission>
                }
              />
              <Route
                path="revisiones/inventario"
                element={
                  <RequirePermission action="view_inspections">
                    <InventarioSaludView />
                  </RequirePermission>
                }
              />
              <Route
                path="revisiones/historial"
                element={
                  <RequirePermission action="view_inspections">
                    <HistorialSesionesView />
                  </RequirePermission>
                }
              />
              {/* Finanzas cuelga de una ruta padre para que el cliente elegido
                  (el tenant sobre el que trabaja el master) sea UNO para toda
                  la sección: se elige en Panorama y el provider sobrevive a la
                  navegación entre pantallas, así que la lista de clientes se
                  pide una vez y no una por página. */}
              <Route
                path="finanzas"
                element={
                  <FinanzasTenantProvider>
                    <Outlet />
                  </FinanzasTenantProvider>
                }
              >
                {/* Sin esto, /admin/finanzas a secas matchea el padre y pinta
                    el <Outlet/> vacío: layout de admin con el contenido en
                    blanco. Antes de anidar caía en la 404; ahora entra por la
                    puerta de la sección, que además es donde se elige cliente. */}
                <Route index element={<Navigate to="/admin/finanzas/dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={
                    <RequirePermission action="view_finanzas">
                      <FinanzasDashboardPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="planilla"
                  element={
                    <RequirePermission action="view_finanzas">
                      <PlanillaPage />
                    </RequirePermission>
                  }
                />
                {/* La carga en bloque dejó de ser una pantalla aparte: vive en
                    la Planilla con un interruptor de modo. El redirect mantiene
                    vivos los enlaces guardados. */}
                <Route
                  path="pagos-bulk"
                  element={<Navigate to="/admin/finanzas/planilla" replace />}
                />
                <Route
                  path="movimientos"
                  element={
                    <RequirePermission action="view_finanzas">
                      <MovimientosPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="mantenimientos"
                  element={
                    <RequirePermission action="view_finanzas">
                      <MantenimientosPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="obligaciones"
                  element={
                    <RequirePermission action="view_finanzas">
                      <ObligacionesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="arqueo"
                  element={
                    <RequirePermission action="view_finanzas">
                      <ArqueoPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="cierre"
                  element={
                    <RequirePermission action="view_finanzas">
                      <CierrePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="unidades"
                  element={
                    <RequirePermission action="manage_finanzas">
                      <UnidadesPage />
                    </RequirePermission>
                  }
                />
              </Route>
              <Route
                path="cobros/clientes"
                element={
                  <RequirePermission action="manage_billing">
                    <ClientesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="cobros/suscripciones"
                element={
                  <RequirePermission action="manage_billing">
                    <SuscripcionesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="cobros/facturas"
                element={
                  <RequirePermission action="manage_billing">
                    <FacturasPage />
                  </RequirePermission>
                }
              />
              <Route
                path="cobros/pagos"
                element={
                  <RequirePermission action="manage_billing">
                    <PagosPage />
                  </RequirePermission>
                }
              />
              <Route
                path="cobros/mora"
                element={
                  <RequirePermission action="manage_billing">
                    <MoraPage />
                  </RequirePermission>
                }
              />
              <Route
                path="cobros/ajustes"
                element={
                  <RequirePermission action="manage_billing">
                    <AjustesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="modulos/gps"
                element={
                  <RequirePermission action="manage_modules">
                    <GpsModulePage />
                  </RequirePermission>
                }
              />
              <Route
                path="modulos/kyc"
                element={
                  <RequirePermission action="manage_modules">
                    <KycModulePage />
                  </RequirePermission>
                }
              />
              <Route
                path="modulos/rutas"
                element={
                  <RequirePermission action="manage_modules">
                    <RutasPage />
                  </RequirePermission>
                }
              />
              <Route
                path="modulos/contabilidad"
                element={
                  <RequirePermission action="manage_modules">
                    <ContabilidadPage />
                  </RequirePermission>
                }
              />
              <Route
                path="plataforma/planes"
                element={
                  <RequirePermission action="catalog.read">
                    <PlanesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="plataforma/features"
                element={
                  <RequirePermission action="catalog.read">
                    <FeaturesPage />
                  </RequirePermission>
                }
              />
            </Route>
            {/*
              Pre-PR-B legacy aliases. These point DIRECTLY at the final
              /admin/flota/* destination — no intermediate /gestion/* hop —
              because /gestion/* itself redirects (above), and chained
              <Navigate replace> on the same render breaks browser history.
            */}
            <Route
              path="/admin/vehicles"
              element={<Navigate to="/admin/flota/vehiculos" replace />}
            />
            <Route
              path="/admin/devices"
              element={<Navigate to="/admin/flota/dispositivos" replace />}
            />
            <Route
              path="/admin/drivers"
              element={<Navigate to="/admin/flota/conductores" replace />}
            />
            <Route
              path="/admin/groups"
              element={<Navigate to="/admin/flota/grupos" replace />}
            />
            <Route
              path="/vehicles"
              element={<Navigate to="/admin/flota/vehiculos" replace />}
            />
            <Route
              path="/devices"
              element={<Navigate to="/admin/flota/dispositivos" replace />}
            />
            <Route
              path="/drivers"
              element={<Navigate to="/admin/flota/conductores" replace />}
            />
            <Route
              path="/geofences"
              element={<Navigate to="/admin/control/geocercas" replace />}
            />

            {/* ── 404 ── */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <BottomNav />
    </div>
    </NavBadgeCountsProvider>
  );
}

/**
 * Adaptador para las pantallas que todavía razonan sobre la flota como array.
 *
 * La suscripción al array completo vive ACÁ y no en `AppChrome`: así el coste
 * (un re-render por cada cambio de cualquier vehículo) lo paga únicamente la
 * ruta que lo necesita, mientras esté montada, y no la app entera. Estas
 * pantallas no conviven con el monitoreo continuo, así que el coste es
 * aceptable; el Monitor sí se suscribe por IMEI.
 */
function FleetListRoute({ render }) {
  const devices = useFleetDevices();
  return render(devices);
}

/** Comandos necesita además el mapa completo de `command_update`. */
function CommandsRoute({ groups }) {
  const devices = useFleetDevices();
  const latestCommandByImei = useFleetCommands();
  return (
    <CommandsPage
      devices={devices}
      latestCommandByImei={latestCommandByImei}
      groups={groups}
    />
  );
}

export default function App() {
  return (
    // Los dos envoltorios quieren estar lo más afuera posible y los dos pueden:
    //
    // `ThemeProvider` porque el tema tiene que estar resuelto también para la
    // pantalla de acceso, el overlay de suspensión y el fallback del
    // ErrorBoundary — pantallas que se ven ANTES de que exista sesión y que
    // igual necesitan pintar con la paleta correcta.
    //
    // `ToastProvider` porque monta el único `ToastContainer` de la app y
    // `useToast()` tira si no lo encuentra, así que ninguna ruta —ni la pública
    // de onboarding, que corre fuera de AuthProvider— puede quedar afuera. Ver
    // `hooks/useToast.jsx`.
    //
    // El orden entre ellos da igual: ThemeProvider no envuelve nada con su
    // estado, escribe `data-theme` sobre <html>, así que los toasts quedan
    // temados estén dentro o fuera. Va primero el tema por ser el más externo
    // de los dos en intención (pinta hasta lo que se rompe).
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <BillingSuspendedOverlay />
          <ErrorBoundary>
            <BrowserRouter>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/onboarding/:slug" element={<OnboardingPage />} />

                  {/* All other routes require authentication */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <WebSocketProvider>
                          <RelativeTimeTickProvider>
                            <AppShell />
                          </RelativeTimeTickProvider>
                        </WebSocketProvider>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
